import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { configurationDate } from './configuration-date';
import { ConfigPages, ConfigProblem } from './ConfigurationParts';
import { purposeHistory, typeHistory, type HistoryPage, type Category, type NoteType } from './api';
type Props = {
  readonly id: string;
  readonly kind: 'purpose' | 'note_type';
  readonly categories?: readonly Category[];
  readonly revision: number;
};
export function ConfigurationHistory({ id, kind, categories = [], revision }: Props) {
  const { t } = useTranslation('expenses');
  const [page, setPage] = useState(1);
  const [retry, setRetry] = useState(0);
  const [history, setHistory] = useState<HistoryPage | null>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true;
    setHistory(null);
    setError('');
    const request =
      kind === 'purpose' ? purposeHistory(id, page) : typeHistory(id as NoteType['noteType'], page);
    void request.then((outcome) => {
      if (!live) return;
      if (outcome.ok) setHistory(outcome.value);
      else setError(outcome.code);
    });
    return () => {
      live = false;
    };
  }, [id, kind, page, retry, revision]);
  let content = <p role="status">{t('configuration.loading')}</p>;
  if (error) content = <ConfigProblem code={error} retry={() => setRetry((n) => n + 1)} />;
  else if (history)
    content = (
      <>
        {history.items.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('configuration.history.none')}</p>
        ) : (
          <ol className="space-y-4">
            {history.items.map((entry) => (
              <HistoryEntry key={entry.id} entry={entry} categories={categories} />
            ))}
          </ol>
        )}
        {history.total > 10 ? (
          <ConfigPages page={page} pageSize={10} total={history.total} onPage={setPage} />
        ) : null}
      </>
    );
  return (
    <section aria-label={t('configuration.history.title')} className="space-y-3">
      <h2 className="font-semibold">{t('configuration.history.title')}</h2>
      {content}
    </section>
  );
}
function HistoryEntry({
  entry,
  categories,
}: {
  readonly entry: HistoryPage['items'][number];
  readonly categories: readonly Category[];
}) {
  const { t, i18n } = useTranslation('expenses');
  const actions: Record<string, string> = {
    'purpose.created': t('configuration.history.created'),
    'purpose.updated': t('configuration.history.updated'),
    'purpose.archived': t('configuration.history.archived'),
    'purpose.restored': t('configuration.history.restored'),
    'purpose.reserved.set': t('configuration.history.reserved'),
    'note_type.categories.set': t('configuration.history.categories'),
  };
  const fields: Record<string, string> = {
    code: t('configuration.code'),
    label: t('configuration.label'),
    active: t('configuration.availability'),
    noteTypes: t('configuration.noteTypes'),
    categoryCodes: t('configuration.expenseTypes'),
    reservedUserIds: t('configuration.people'),
  };
  function shown(key: string, value: unknown): string {
    if (value === null) return t('configuration.history.empty');
    if (key === 'reservedUserIds' && Array.isArray(value))
      return t('configuration.history.people', { count: value.length });
    if (typeof value === 'boolean') return t(`configuration.${value ? 'active' : 'archived'}`);
    if (Array.isArray(value))
      return (
        value
          .map((v) => {
            if (key === 'noteTypes') return t(`reports.noteType.${v as NoteType['noteType']}`);
            const c = categories.find((c) => c.code === v);
            if (!c) return String(v);
            return i18n.language.startsWith('en') ? c.labelEn : c.labelIt;
          })
          .join(', ') || t('configuration.history.empty')
      );
    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : t('configuration.history.updated');
  }
  const diff =
    typeof entry.diff === 'object' && entry.diff !== null ? Object.entries(entry.diff) : [];
  return (
    <li className="border-border space-y-1 border-l-2 pl-3 text-sm">
      <p className="font-semibold">{actions[entry.action] ?? t('configuration.history.updated')}</p>
      <p className="text-muted-foreground">
        {entry.actorDisplayName} · {configurationDate(entry.at)}
      </p>
      {diff.map(([key, values]) =>
        Array.isArray(values) && values.length === 2 ? (
          <p key={key} className="break-words">
            {fields[key] ?? t('configuration.history.updated')}: {shown(key, values[0])} →{' '}
            {shown(key, values[1])}
          </p>
        ) : null,
      )}
    </li>
  );
}
